/**
 * Matchmaking helper functions
 * 
 * Provides core utilities for mapping backend provider data to the format
 * expected by the UI. Handles transformation of provider profiles, match results,
 * and API responses.
 */

import type { ApiProviderProfile, MappedMatch } from '../types/matchmaking.types';

/**
 * Match result structure from the matchmaking API
 * Contains provider ID, reason, and optional badges
 */
export type ApiMatchResult = {
  provider_id: string;
  reason: string;
  badges?: string[];
};

/**
 * Complete API response structure from the backend matchmaking proxy
 * Contains match results, provider profiles, and metadata
 */
export type ApiResponse = {
  time_taken: number;
  result: Record<string, ApiMatchResult> | { message?: string };
  provider_profiles: Record<string, ApiProviderProfile>;
  status_code?: number;
};

/**
 * Maps backend provider data to API response format
 * 
 * Transforms provider objects from backend into the format expected by
 * the matchmaking API response structure. Handles service area formatting,
 * distance parsing, and field mapping.
 * 
 * @param providers - Array of provider objects from backend
 * @returns API response structure with result and provider_profiles
 */
export const mapBackendProvidersToApiResponse = (providers: any[]): ApiResponse => {
  const result: Record<string, ApiMatchResult> = {};
  const provider_profiles: Record<string, ApiProviderProfile> = {};

  providers.forEach((provider, index) => {
    const providerId = provider.providerId || provider.providerID;
    if (!providerId) return;

    result[(index + 1).toString()] = {
      provider_id: providerId,
      reason: provider?.aiData?.aiRationale || provider?.aiData?.ai_rationale || '',
      badges: provider?.aiData?.badges || provider.badges || [],
    };

    let serviceAreaString = '-';
    if (provider.serviceArea && typeof provider.serviceArea === 'object') {
      const states: string[] = [];
      Object.entries(provider.serviceArea).forEach(([state, counties]: [string, any]) => {
        if (state) {
          if (Array.isArray(counties) && counties.length > 0) {
            states.push(`${state} (${counties.join(', ')})`);
          } else {
            states.push(state);
          }
        }
      });
      serviceAreaString = states.length > 0 ? states.join('; ') : '-';
    }

    const rawDistance =
      provider.distanceMiles ??
      provider.distance_miles ??
      provider.distance ??
      provider.distanceInMiles;
    const parsedDistance =
      typeof rawDistance === 'string' ? parseFloat(rawDistance) : rawDistance;

    const mappedProfile: ApiProviderProfile & { name?: string; serviceArea?: any; aiRationale?: string; distanceMiles?: number } = {
      provider_id: providerId,
      acceptsNewClients: true,
      categories: provider.categories || [],
      specialties: provider.specialties || [],
      expertise_areas: provider.expertise || [],
      serviceOfferings: provider.serviceOfferings,
      expertise: provider.expertise,
      companyOverview: provider.companyOverview,
      latitude: provider.latitude || 0,
      longitude: provider.longitude || 0,
      bio: provider.bio,
      rating: provider.google_reviews_rating || provider.rating || 0,
      reviewsTotalNumber: provider.reviewsTotalNumber || 0,
      years_experience: provider.years_experience || 0,
      professional_associations: provider.professional_associations,
      notable_specialty_certifications: provider.notable_specialty_certifications,
      professional_certifications: provider.professional_certifications,
      typical_cost_range_min: provider.typical_cost_range_min || 0,
      typical_cost_range_max: provider.typical_cost_range_max || 0,
      cost_unit: provider.cost_unit || '',
      pricing_model: provider.pricing_model || '',
      registeredStates: provider.registeredStates || [],
      service_ranges: provider.service_ranges || [],
      licensedRegions: provider.licensedRegions || [],
      professionalLicenses: provider.professionalLicenses,
      certifications: provider.certifications,
      awardsOrRecognitions: provider.awardsOrRecognitions,
      emergency_response_available: provider.emergency_response_available || false,
      availability_schedule: provider.availability_schedule || '',
      doc_type: provider.doc_type || '',
      semantic_summary: provider.semantic_summary,
      search_text: provider.search_text,
      badges: provider?.aiData?.badges || provider.badges || [],
      name: provider.companyName,
      serviceArea: serviceAreaString,
      ...(provider.aiData?.aiRationale && { aiRationale: provider.aiData.aiRationale }),
      ...(Number.isFinite(parsedDistance) && { distanceMiles: parsedDistance }),
    };

    provider_profiles[providerId] = mappedProfile;
  });

  return {
    time_taken: 0,
    result,
    provider_profiles,
  };
};

/**
 * Maps API response to match format for UI display
 * 
 * Transforms API response into MappedMatch objects with computed match levels,
 * formatted names, tags, service areas, and AI rationale.
 * 
 * @param apiResponse - API response from the backend matchmaking proxy
 * @returns Array of mapped matches ready for UI display
 */
export const mapApiResponseToMatches = (apiResponse: ApiResponse): MappedMatch[] => {
  if (
    !apiResponse.result ||
    typeof apiResponse.result !== 'object' ||
    'message' in apiResponse.result
  ) {
    return [];
  }

  const castResult = apiResponse.result as Record<string, ApiMatchResult>;
  const resultEntries = Object.entries(castResult)
    .map(([key, value]) => ({ rank: parseInt(key), ...value }))
    .sort((a, b) => a.rank - b.rank);

  const totalResults = resultEntries.length;

  return resultEntries.map((resultItem, index) => {
    const providerId = resultItem.provider_id;
    const profile = apiResponse.provider_profiles[providerId];

    if (!profile) {
      return {
        id: providerId,
        name: providerId,
        tags: [],
        serviceArea: '-',
        serviceType: '-',
        aiRationale: resultItem.reason || '',
        matchLevel: 'medium' as const,
        matchLabel: 'Medium Match',
      };
    }

    let matchLevel: 'high' | 'medium' | 'low' = 'medium';
    let matchLabel = 'Medium Match';

    if (index === 0 || (totalResults <= 3 && index < 2)) {
      matchLevel = 'high';
      matchLabel = 'High Match';
    } else if (index === totalResults - 1 && totalResults > 2) {
      matchLevel = 'low';
      matchLabel = 'Low Match';
    }

    let name = `Provider ${providerId}`;
    if ((profile as any).name) {
      name = (profile as any).name;
    } else if (profile.companyOverview) {
      const overview = profile.companyOverview;
      const stopWords = ['offers', 'provides', 'helps', 'specializes'];
      let companyName = overview;
      for (const stopWord of stopWords) {
        const index = overview.toLowerCase().indexOf(` ${stopWord}`);
        if (index > 0) {
          companyName = overview.substring(0, index).trim();
          break;
        }
      }
      name = companyName || overview.split(' ').slice(0, 4).join(' ');
    } else if (profile.bio) {
      name = profile.bio.split(' ').slice(0, 4).join(' ');
    }

    const tags = [
      ...(profile.categories || []),
      ...(profile.specialties || []),
    ];

    let serviceArea = '-';
    if ((profile as any).serviceArea) {
      serviceArea = (profile as any).serviceArea;
    } else if (profile.licensedRegions && profile.licensedRegions.length > 0) {
      serviceArea = profile.licensedRegions.join(', ');
    } else if (profile.registeredStates && profile.registeredStates.length > 0) {
      serviceArea = profile.registeredStates.join(', ');
    }

    const serviceType =
      profile.service_ranges && profile.service_ranges.length > 0
        ? profile.service_ranges
            .map((range) =>
              range
                .split(' ')
                .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                .join(' '),
            )
            .join(', ')
        : '-';

    const aiRationale = (profile as any).aiRationale || resultItem.reason;
    const badges = resultItem.badges || profile.badges || [];

    return {
      id: providerId,
      name,
      tags,
      serviceArea,
      serviceType,
      aiRationale: aiRationale,
      matchLevel,
      matchLabel,
      providerProfile: {
        ...profile,
        badges: badges,
      },
    };
  });
};

